/*
 * Sticky add-to-cart bar.
 * Shows when the section's product form is scrolled out of view; the button
 * delegates to the form's own submit (button[name="add"]) so variant,
 * quantity and selling-plan state stay in sync. Never queries global IDs.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.submitButton = this.querySelector('[data-sticky-atc-submit]');
    if (!this.sectionId || !this.submitButton) return;

    this.form = document.querySelector(`#MainProduct-${this.sectionId} product-form form`);
    if (!this.form) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.toggleVisibility(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.form);

    this.submitButton.addEventListener('click', () => {
      const addButton = this.form.querySelector('button[name="add"]');
      if (addButton && !addButton.disabled) addButton.click();
    });

    // Keep the bar's disabled/available state in sync with the real button.
    this.syncObserver = new MutationObserver(() => this.syncState());
    const addButton = this.form.querySelector('button[name="add"]');
    if (addButton) {
      this.syncObserver.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
    }
    this.syncState();

    // Keep the bar's price in sync when the variant changes. Dawn swaps the
    // whole #price-<id> wrapper on variant selection, so observe the stable
    // product section and re-resolve .price on every mutation. The innerHTML
    // equality check prevents the observer from re-triggering on our own write.
    this.priceTarget = this.querySelector('[data-sticky-atc-price]');
    this.productSection = document.querySelector(`#MainProduct-${this.sectionId}`);
    this.priceObserver = new MutationObserver(() => this.syncPrice());
    if (this.priceTarget && this.productSection) {
      this.priceObserver.observe(this.productSection, { childList: true, subtree: true, characterData: true });
      this.syncPrice();
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.syncObserver) this.syncObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
  }

  syncPrice() {
    const source = this.productSection ? this.productSection.querySelector('.price') : null;
    if (!source || !this.priceTarget) return;
    const next = source.innerHTML;
    if (this.priceTarget.innerHTML === next) return;
    this.priceTarget.innerHTML = next;
  }

  toggleVisibility(show) {
    this.classList.toggle('sticky-atc--visible', show);
    this.setAttribute('aria-hidden', String(!show));
  }

  syncState() {
    const addButton = this.form.querySelector('button[name="add"]');
    if (addButton) this.submitButton.disabled = addButton.disabled;
  }
}

customElements.define('sticky-atc', StickyAtc);
